import React from 'react';
import { useMutation } from 'react-query';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import ActivityAssociations from 'v2/common/Activity/ActivityAssociations';
import { OverlayLayout } from 'v2/common/Activity/AssociationsLayout';
import {
  Activity,
  ActivityAssociation,
  File,
  GenericObject,
  User,
} from 'v2/models';
import { useActivityInfo } from 'v2/utils/activityUtils';
import restFactory from 'v2/utils/restFactory';
import pluralize from 'pluralize-esm';

interface StateProps {
  user: User;
}

interface ActivityItemAssociationsProps extends StateProps {
  activity?: Activity;
  file?: File;
  refetch: () => void;
  disabled: boolean;
  defaultAssociations: ActivityAssociation[];
}

const ActivityItemAssociations = ({
  activity,
  user,
  refetch,
  file,
  defaultAssociations,
  disabled: isDisabled,
}: ActivityItemAssociationsProps) => {
  const ActivityInfo = useActivityInfo();
  const { mutate: updateAssociationsMutation, isLoading } = useMutation({
    mutationKey: [
      'ActivityItemAssociations_UpdateAssociations',
      `activity_query_${activity?.id || file?.id}`,
    ],
    mutationFn: async (associations?: ActivityAssociation[]) => {
      const fileTemp = { ...file };
      if (fileTemp) {
        delete fileTemp.file;
      }
      await restFactory.patch(
        `/dj/api/v1/${file ? 'file/' + file.id : 'activity/' + activity?.id}/`,
        {
          ...(file ? fileTemp : activity),
          relationships: associations,
        }
      );
      return associations;
    },
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      toast.error('At least one relationship needed. Please check one.');
    },
  });

  const handleAssociationsChange = (
    allCheckedFields?: ActivityAssociation[]
  ) => {
    const viewableAssociations = allCheckedFields?.filter(
      (association) =>
        association.is_viewable === true ||
        association.is_viewable === undefined
    );
    updateAssociationsMutation(viewableAssociations);
  };

  function getActivityType(activity: Activity | undefined) {
    if (activity && ActivityInfo[activity.type]) {
      return pluralize(ActivityInfo[activity.type].title);
    }
    return '';
  }

  const isActivityEditable =
    !isDisabled &&
    !isLoading &&
    (file?.owner.id === user.id || activity?.is_editable === true);

  return (
    <ActivityAssociations
      activity={activity}
      onChange={handleAssociationsChange}
      Layout={OverlayLayout}
      readOnly={!isActivityEditable}
      headerLegend="association"
      activityType={getActivityType(activity)}
      file={file}
      initialActivityAssociations={defaultAssociations}
    />
  );
};

const mapStateToProps = (state: GenericObject) => {
  return {
    user: state.user.user,
  };
};

export default connect<StateProps>(mapStateToProps)(ActivityItemAssociations);
